/**
 * What a company files to the STANDINGS (server/standingsApi.mjs), and when.
 *
 * The server never opens a save, so it cannot work out anybody's highest rank by reading profiles.
 * Each device therefore derives its own six numbers here and sends them. Six numbers is the whole
 * of it: no name, no callsign, no id. That is why this needs no opt-in the way the public wall's
 * names do - if something in here ever starts identifying a man, it has become a different feature.
 */

import { everyRanger } from '../../meta/records';
import type { ProfileState } from '../../meta/state';
import type { RangerRecord } from '../../meta/types';

/** One company's own figures. The server clamps every one of these; nothing else is sent. */
export interface StandingsRecord {
  /** The highest `ranger.rank` this profile has ever seen: an index into RANKS. */
  highestRank: number;
  /** Missions flown by the one man who has flown the most. */
  mostMissions: number;
  /** Medals worn by the one man who has the most. */
  mostMedals: number;
  /** Longest run of successful missions by one ranger. */
  bestStreak: number;
  rangersLost: number;
  operationsWon: number;
}

/** The standings as the server folds them: the best of the bests, the sum of the totals. */
export interface Standings extends StandingsRecord {
  /** How many companies are counted in the figures above. */
  saves: number;
}

/**
 * This company's figures, from the profile alone. Pure.
 *
 * `everyRanger` takes in the memorial as well as the living, and the fallen COUNT: a dead man's
 * rank was still reached, and dropping him would make the ladder look shorter the worse a player
 * had done. The same goes for his missions and his medals - they were flown and they were won.
 */
export function standingsFrom(profile: ProfileState): StandingsRecord {
  const rangers = everyRanger(profile);
  const best = (measure: (r: RangerRecord) => number): number =>
    rangers.reduce((top, r) => Math.max(top, measure(r)), 0);
  return {
    highestRank: best((r) => r.rank),
    mostMissions: best((r) => r.record.missions),
    mostMedals: best((r) => r.medals.length),
    bestStreak: profile.bestStreak,
    rangersLost: profile.memorial.length,
    operationsWon: profile.operationsWon,
  };
}

/** Whether two records say the same thing: what "file it when it changes" is measured against. Pure. */
export function sameStanding(a: StandingsRecord, b: StandingsRecord): boolean {
  return a.highestRank === b.highestRank && a.mostMissions === b.mostMissions
    && a.mostMedals === b.mostMedals && a.bestStreak === b.bestStreak
    && a.rangersLost === b.rangersLost && a.operationsWon === b.operationsWon;
}

export interface ReporterDeps {
  enabled(): boolean;
  code(): string | null;
  /** The profile as it stands on this device; null when there is none to read yet. */
  profile(): ProfileState | null;
  /** Send it. True when the server took it, false for anything else - a refusal or a dead server. */
  file(code: string, record: StandingsRecord): Promise<boolean>;
}

export interface StandingsReporter {
  /** File this company's figures, unless the server already has exactly these. Never rejects. */
  report(): Promise<void>;
}

export function createStandingsReporter(deps: ReporterDeps): StandingsReporter {
  /** The last record the server ACCEPTED, so an unchanged company files once and then stops. */
  let filed: StandingsRecord | null = null;
  let sending = false;

  return {
    async report() {
      const code = deps.code();
      if (sending || !deps.enabled() || code === null) return;
      const profile = deps.profile();
      if (profile === null) return;
      const record = standingsFrom(profile);
      if (filed !== null && sameStanding(filed, record)) return;
      sending = true;
      try {
        // Only a filing the server took is remembered. A refusal (no save up there yet) or a
        // server that said nothing is simply tried again at the next save, which is the next
        // moment anything could have changed anyway - so there is nothing here to retry with.
        if (await deps.file(code, record)) filed = record;
      } catch {
        // The standings are an extra. Nothing about them may reach the player as a failure.
      } finally {
        sending = false;
      }
    },
  };
}
